import { getSelectedCompany, getUserToken } from 'context/authentication/User.helpers'
import { isPublicPath } from 'global/helpers/authorization'
import { getBaseApiUrl } from 'global/helpers/environment'
import { appHistory } from 'global/helpers/history'
import { createServerError, getResponseMediaType, isJsonResponse } from 'global/helpers/requestError'
import { type PaginatedResults } from 'global/types/pagination'

export const IDENTITY_PREFIX = 'identity'
export const TRIP_PREFIX = 'trip'
export const ROUTES_PREFIX = 'routes'
export const FILE_PREFIX = 'status-reporting'
export const TELEMATICS_PREFIX = 'telematics'
export const GEOFENCE_PREFIX = 'movement-monitoring'
export const CARRIER_PREFIX = 'carrier'
export const EMAIL_SCHEDULING_PREFIX = 'email-scheduling'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type WithId<T> = T & {
  id: string
}

type RequestOptions = {
  method?: HttpMethod
  token?: string
  headers?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
}

const baseRequest = async <T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> => {
  const baseApiUrl = getBaseApiUrl()
  const apiUrl = prefix
    ? `${baseApiUrl}/api/${prefix}/${import.meta.env.VITE_API_VERSION}/${endpoint}`
    : `${baseApiUrl}/api/${import.meta.env.VITE_API_VERSION}/${endpoint}`

  const token = options.token ?? await getUserToken()
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  }

  const response = await fetch(apiUrl, {
    method: options.method,
    headers,
    body: options.method === 'GET' ? undefined : options.body as BodyInit,
    signal: options.signal,
  })

  if (!response.ok) {
    if (response.status === 401 && !isPublicPath(appHistory.location.pathname)) {
      const { pathname, search, hash } = appHistory.location
      const redirectParams = new URLSearchParams({
        redirect: `${pathname}${search}${hash}`,
      })
      const selectedCompanyCid = getSelectedCompany()?.cid
      if (selectedCompanyCid) {
        redirectParams.set('company', selectedCompanyCid)
      }
      appHistory.replace({
        pathname: '/login',
        search: `?${redirectParams.toString()}`,
      })
    }
    throw await createServerError(response)
  }

  let data: unknown
  const mediaType = getResponseMediaType(response)
  const isBlob = mediaType === 'application/octet-stream'
    || mediaType === 'application/pdf'
    || mediaType?.startsWith('image/')
  if (response.status === 204) {
    data = undefined
  } else if (isJsonResponse(response)) {
    data = await response.json()
  } else if (isBlob) {
    data = await response.blob()
  } else {
    data = await response.text()
  }

  return data as T
}

export const getRequest = async <T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> =>
  baseRequest(endpoint, { ...options, method: 'GET' }, prefix)

const cleanUpJsonBody = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    return trimmedValue === '' ? null : trimmedValue
  }
  if (Array.isArray(value)) {
    return value.map(cleanUpJsonBody)
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, cleanUpJsonBody(nestedValue)]),
    )
  }
  return value
}

const baseMutationRequest = async <T = unknown>(
  method: Exclude<HttpMethod, 'GET'>,
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> => {
  if (options.body instanceof FormData) {
    return baseRequest(endpoint, {
      ...options,
      method,
    }, prefix)
  }
  return baseRequest(endpoint, {
    ...options,
    method,
    body: JSON.stringify(cleanUpJsonBody(options.body)),
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }, prefix)
}

export const postRequest = async <T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> =>
  baseMutationRequest('POST', endpoint, options, prefix)

export const putRequest = async <T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> =>
  baseMutationRequest('PUT', endpoint, options, prefix)

export const deleteRequest = async <T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
  prefix?: string,
): Promise<T> =>
  baseMutationRequest('DELETE', endpoint, options, prefix)

export const getChunkedResults = async <T extends object>(
  getPaginatedResults: (page: number, pageSize: number) => Promise<PaginatedResults<T>>,
  pageSize = 1000,
): Promise<T[]> => {
  const firstPage = await getPaginatedResults(0, pageSize)
  const pageCount = Math.ceil(firstPage.totalElements / pageSize)
  if (pageCount === 1) {
    return firstPage.content
  }
  const chunkedRequests: Promise<PaginatedResults<T>>[] = []
  for (let page = 1; page < pageCount; page++) {
    chunkedRequests.push(getPaginatedResults(page, pageSize))
  }
  const chunkedResults = await Promise.all(chunkedRequests)
  return [
    ...firstPage.content,
    ...chunkedResults.reduce(
      (results: T[], paginatedChunk: PaginatedResults<T>) => results.concat(paginatedChunk.content),
      [],
    ),
  ]
}
